import React, { useEffect, useRef, FC, useMemo } from "react";
import { ItemProps } from "../../types";
import { v4 as uuidv4 } from "uuid";
import CreatableSelect from "react-select/creatable";
import SelectComponent, { InputActionMeta, ActionMeta, SingleValue, MultiValue } from "react-select";

import loadOptions from "../../utils/select/loadOptions";
import style from "../../utils/select/style";
import customFilterOption from "../../utils/select/customFilterOption";
import CustomSingleSelect from "../../utils/select/CustomSingleSelect";
import onMenuOpen from "../../utils/select/onMenuOpen";
import ValidationError from "@/components/Form/elements/ValidationError";

const Select: FC<ItemProps> = (
	{
		formItem,
		validation,
		disabled,
		formData,
		onChange,
		updateForm
	}
) => {
	const [options, setOptions] = React.useState<any[] | []>([]);
	const [loading, setLoading] = React.useState<boolean>(false);
	const [loaded, setLoaded] = React.useState<boolean>(false);
	const [uuid, setUuid] = React.useState<string>(uuidv4());

	const selectRef = useRef<any>(null);

	const fetchOptions = async (inputValue?: string | null | undefined) => {
		setLoading(true);
		const loadedOptions = await loadOptions(formItem, inputValue || "", formData);
		setOptions(loadedOptions);
		setLoading(false);
		setLoaded(true);
	};

	useEffect(() => {
		if (formItem.itemOptions?.features?.needToUpdateOnOpen) {
			fetchOptions();
		} else {
			if (!loaded) fetchOptions();
		}
	}, [formItem]);

	const handleInputChange = (newValue: string, actionMeta: InputActionMeta) => {
		if (formItem.itemOptions?.features?.needToUpdateOnOpen) fetchOptions(newValue);
	};

	const handleSelectChange = (newData: SingleValue<any> | MultiValue<any>, actionMeta: ActionMeta<any>) => {
		onChange?.(formItem.name, newData);
	};

	const handleOnnMenuOpen = () => onMenuOpen(uuid);

	const allowCreate = useMemo(() => !!formItem.itemOptions?.allowCreate, [formItem.itemOptions?.allowCreate]);
	const CommonSelect = useMemo(() => (allowCreate ? CreatableSelect : SelectComponent), [allowCreate]);

	const isMulti = !!formItem.itemOptions?.features?.isMulti;

	// ✅ normalize incoming data (single object OR array OR primitive) -> array of values
	const raw = formData?.[formItem?.name];
	const selectedValues = useMemo(() => {
		if (isMulti) {
			if (Array.isArray(raw)) return raw.map(v => (v?.value ?? v)).filter(Boolean);
			if (raw?.value != null) return [raw.value];
			if (raw != null) return [raw];
			return [];
		}

		if (raw?.value != null) return raw.value;
		return raw ?? null;
	}, [raw, isMulti]);

	// ✅ compute react-select value from options
	const selectValue = useMemo(() => {
		if (isMulti) return options.filter(o => selectedValues.includes(o?.value));
		return options.find(o => o?.value === selectedValues) || null;
	}, [options, selectedValues, isMulti]);

	const handleOnCreateOption = (inputValue: string) => {
		if (!allowCreate) return;

		const newOption = { label: inputValue, value: inputValue };
		const newOptions = [...options, newOption];
		setOptions(newOptions);
		updateForm?.(formItem.name, "list", newOptions);

		if (isMulti) {
			const next = [...(Array.isArray(selectValue) ? selectValue : []), newOption];
			onChange?.(formItem.name, next);
		} else {
			onChange?.(formItem.name, newOption);
		}
	};

	useEffect(() => {
		if (!uuid) setUuid(uuidv4());
	}, []);

	return (
		<>
			<div className={`!px-0 min-h-[43px] ${formItem.classNames?.content || ""} `}>
				<CommonSelect
					ref={selectRef}
					isClearable={formItem.itemOptions?.clearable || false}
					isDisabled={loaded ? disabled : true}
					isLoading={loading}
					options={options}
					value={selectValue}
					placeholder={formItem?.placeholder || "Select an option"}
					onChange={handleSelectChange}
					onInputChange={handleInputChange}
					onMenuOpen={handleOnnMenuOpen}
					onCreateOption={handleOnCreateOption}
					className="h-full !min-w-full block !bg-transparent"
					styles={style({ validation, item: formItem })}
					filterOption={(option, inputValue) => customFilterOption(option, inputValue)}
					components={{
						IndicatorSeparator: null,
						SingleValue: CustomSingleSelect,
						...(formItem.itemOptions?.components || {})
					}}
					{...formItem.itemOptions?.features}
					menuPosition="fixed"
					menuShouldScrollIntoView
					menuPortalTarget={document.body}
					classNamePrefix={`Select_${uuid}`}
				/>
			</div>

			<ValidationError error={validation} formItem={formItem} />
		</>
	);
};

export default Select;